import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, fmtNum, fmtCur, downloadExport, downloadPdf, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PeriodFilter from "@/components/PeriodFilter";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Search, Download, ChevronRight, Trash2, Loader2, Pencil, FileText } from "lucide-react";
import { toast } from "sonner";

const SORTS = [
  { key: "clients", label: "Clients" },
  { key: "deposited", label: "Deposited" },
  { key: "verified", label: "Verified" },
  { key: "pending", label: "Pending" },
];

export default function Referrals() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("clients");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [delTarget, setDelTarget] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const [busy, setBusy] = useState(false);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["referrals", q, sort, dateFrom, dateTo],
    queryFn: () => api.get("/referrals", { params: { q, sort, date_from: dateFrom, date_to: dateTo } }).then((r) => r.data),
  });

  const handleExport = async () => {
    try {
      await downloadExport({}, "csv");
      toast.success("Export started");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const [pdfBusy, setPdfBusy] = useState(false);
  const handlePdf = async () => {
    setPdfBusy(true);
    try {
      await downloadPdf({});
      toast.success("PDF report generated");
    } catch (e) {
      toast.error("PDF export failed");
    } finally {
      setPdfBusy(false);
    }
  };

  const deleteAffiliate = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/referrals/${encodeURIComponent(delTarget.referral)}`);
      toast.success(`Deleted affiliate ${delTarget.referral} and ${data.deleted_customers} client(s)`);
      setDelTarget(null);
      qc.invalidateQueries();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const renameAffiliate = async () => {
    setBusy(true);
    try {
      await api.patch(`/referrals/${encodeURIComponent(renameTarget.referral)}`, { new_code: renameVal.trim() });
      toast.success(`Renamed to ${renameVal.trim()}`);
      setRenameTarget(null);
      qc.invalidateQueries();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="referrals-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight">Referral Ranking</h1>
          <p className="mt-1 text-sm text-muted-foreground">Performance of every referral / IB code.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleExport} data-testid="export-referrals">
            <Download className="mr-2 h-4 w-4" /> Export all clients
          </Button>
          <Button onClick={handlePdf} disabled={pdfBusy} data-testid="export-referrals-pdf">
            {pdfBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />} PDF report
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search code or name..."
            className="pl-9"
            data-testid="referral-search"
          />
        </div>
        <div className="flex items-center gap-1 rounded-md border border-border bg-card p-1">
          {SORTS.map((s) => (
            <button
              key={s.key}
              onClick={() => setSort(s.key)}
              data-testid={`sort-${s.key}`}
              className={`rounded px-3 py-1.5 text-xs font-medium transition-colors ${
                sort === s.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <PeriodFilter dateFrom={dateFrom} dateTo={dateTo} onChange={(f, t) => { setDateFrom(f); setDateTo(t); }} testId="referrals-period" />

      {/* Mobile: cards */}
      <div className="space-y-3 md:hidden" data-testid="referral-cards">
        {isLoading ? (
          [...Array(5)].map((_, i) => <div key={i} className="h-24 animate-pulse rounded-md border border-border bg-[#E6EFF2]" />)
        ) : rows.length === 0 ? (
          <div className="rounded-md border border-border bg-card px-4 py-10 text-center text-muted-foreground" data-testid="referral-cards-empty">
            No referrals found.
          </div>
        ) : (
          rows.map((r) => (
            <div
              key={r.referral}
              onClick={() => navigate(`/referrals/${encodeURIComponent(r.referral)}`)}
              className="rounded-md border border-border bg-card p-4"
              data-testid={`referral-card-${r.referral}`}
            >
              <div className="mb-3 flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-primary/10 text-xs font-semibold text-primary">{r.rank}</span>
                  <div className="min-w-0">
                    {r.name && <div className="truncate text-sm font-medium">{r.name}</div>}
                    <div className="truncate font-mono text-xs text-muted-foreground">{r.referral}</div>
                  </div>
                </div>
                {isAdmin && (
                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); setRenameTarget(r); setRenameVal(r.referral); }}
                      className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-[#2b9bb7]/10 hover:text-[#2b9bb7]"
                      title="Rename"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setDelTarget(r); }}
                      className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                <div><div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Clients</div><div className="font-mono text-sm font-semibold">{fmtNum(r.clients)}</div></div>
                <div><div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Deposited</div><div className="font-mono text-sm">{fmtCur(r.deposited)}</div></div>
                <div><div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Verified</div><div className="font-mono text-sm text-[#326F4B]">{fmtNum(r.verified)}</div></div>
                <div><div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Pending</div><div className="font-mono text-sm text-[#A7773F]">{fmtNum(r.pending)}</div></div>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="hidden overflow-x-auto rounded-md border border-border bg-card md:block">
        <table className="w-full text-sm" data-testid="referral-table">
          <thead>
            <tr className="border-b border-border bg-[#EDF3F5] text-left">
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Rank</th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Referral</th>
              <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">Clients</th>
              <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">Deposited</th>
              <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">Verified</th>
              <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">Pending</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              [...Array(8)].map((_, i) => (
                <tr key={i} className="border-b border-border">
                  <td colSpan={7} className="px-4 py-3">
                    <div className="h-4 w-full animate-pulse rounded bg-[#E6EFF2]" />
                  </td>
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-muted-foreground" data-testid="referral-empty">
                  No referrals found.
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr
                  key={r.referral}
                  onClick={() => navigate(`/referrals/${encodeURIComponent(r.referral)}`)}
                  className="cursor-pointer border-b border-border transition-colors duration-150 hover:bg-[#E6EFF2]"
                  data-testid={`referral-row-${r.referral}`}
                >
                  <td className="px-4 py-3">
                    <span className="flex h-6 w-6 items-center justify-center rounded bg-primary/10 text-xs font-semibold text-primary">
                      {r.rank}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {r.name ? (
                      <div>
                        <div className="font-medium">{r.name}</div>
                        <div className="font-mono text-xs text-muted-foreground">{r.referral}</div>
                      </div>
                    ) : (
                      <span className="font-mono font-medium">{r.referral}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-mono tabular font-semibold">{fmtNum(r.clients)}</td>
                  <td className="px-4 py-3 text-right font-mono tabular">{fmtCur(r.deposited)}</td>
                  <td className="px-4 py-3 text-right font-mono tabular text-[#326F4B]">{fmtNum(r.verified)}</td>
                  <td className="px-4 py-3 text-right font-mono tabular text-[#A7773F]">{fmtNum(r.pending)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {isAdmin && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setRenameTarget(r); setRenameVal(r.referral); }}
                          data-testid={`rename-referral-${r.referral}`}
                          className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-[#2b9bb7]/10 hover:text-[#2b9bb7]"
                          title="Rename affiliate code"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                      )}
                      {isAdmin && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setDelTarget(r); }}
                          data-testid={`delete-referral-${r.referral}`}
                          className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                          title="Delete affiliate and its clients"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Delete affiliate confirm */}
      <Dialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent data-testid="delete-referral-dialog">
          <DialogHeader>
            <DialogTitle>Delete affiliate {delTarget?.referral}?</DialogTitle>
            <DialogDescription>
              This permanently removes the affiliate and its <span className="font-semibold text-foreground">{fmtNum(delTarget?.clients)}</span> client record(s) from all reports, plus any custom commission rate. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={deleteAffiliate} disabled={busy} data-testid="confirm-delete-referral">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Delete affiliate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename affiliate */}
      <Dialog open={!!renameTarget} onOpenChange={(o) => !o && setRenameTarget(null)}>
        <DialogContent data-testid="rename-referral-dialog">
          <DialogHeader>
            <DialogTitle>Rename affiliate {renameTarget?.referral}</DialogTitle>
            <DialogDescription>
              Updates the referral code on all {fmtNum(renameTarget?.clients)} client(s) and moves any custom commission rate.
            </DialogDescription>
          </DialogHeader>
          <Input value={renameVal} onChange={(e) => setRenameVal(e.target.value)} placeholder="New affiliate code" data-testid="rename-referral-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameTarget(null)}>Cancel</Button>
            <Button onClick={renameAffiliate} disabled={busy || !renameVal.trim()} data-testid="confirm-rename-referral">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Rename
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
